import { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { TrendUp, CheckCircle, Lightning, WarningCircle, Target } from "@phosphor-icons/react";

const COLORS = ["#10B981", "#EAB308", "#F59E0B", "#DC2626", "#6B7280"];
const BAND_COLOR = {
    scarta: "#DC2626", alto_rischio: "#F59E0B",
    interessante: "#EAB308", ottima: "#10B981", unknown: "#9CA3AF",
};
const BAND_LABEL = {
    scarta: "Scartare", alto_rischio: "Alto rischio",
    interessante: "Interessante", ottima: "Ottima opportunità", unknown: "—",
};

const AGENT_LABEL = {
    document_reader: "Document Reader", legal_risk: "Legal Risk",
    urban_planning: "Urban Planning", market_valuation: "Market Valuation",
    investment: "Investment", action_planner: "Action Planner", report: "Report",
};

function qualityColor(q) {
    if (q == null) return "text-gray-400";
    if (q >= 80) return "text-emerald-600";
    if (q >= 60) return "text-yellow-700";
    if (q >= 40) return "text-orange-600";
    return "text-red-600";
}

export default function KPIDashboard() {
    const [data, setData] = useState(null);
    const [trends, setTrends] = useState(null);
    const [days, setDays] = useState(30);
    const [drillCategory, setDrillCategory] = useState(null);
    const [drillData, setDrillData] = useState(null);

    const load = async () => {
        const [k, t] = await Promise.all([
            api.get("/validation/kpi", { params: { days } }),
            api.get("/validation/trends", { params: { days } }),
        ]);
        setData(k.data);
        setTrends(t.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    const openDrill = async (category) => {
        setDrillCategory(category);
        const { data: d } = await api.get(`/validation/errors/${category}`, { params: { days } });
        setDrillData(d);
    };

    if (!data) return <Layout><div className="text-sm text-gray-500">Caricamento…</div></Layout>;

    const exec = data.executive;
    const agentQ = data.agent_quality;

    const bandData = Object.entries(data.score_band_distribution || {}).map(([k, v]) => ({
        name: BAND_LABEL[k] || k, value: v, color: BAND_COLOR[k] || "#9CA3AF",
    }));

    const agentRows = Object.entries(data.agents || {}).map(([agent, s]) => ({
        agent: AGENT_LABEL[agent] || agent,
        success_rate: s.success_rate || 0,
        retry_rate: s.retry_rate || 0,
        avg_seconds: Math.round((s.avg_ms || 0) / 1000),
    }));

    return (
        <Layout>
            <div className="flex items-end justify-between mb-8" data-testid="kpi-header">
                <div>
                    <div className="overline mb-2">KPI Dashboard</div>
                    <h1 className="font-display-black text-4xl sm:text-5xl leading-none">
                        Agent accuracy.
                    </h1>
                    <p className="text-sm text-gray-600 mt-3 max-w-xl">
                        Metriche di affidabilità della pipeline AI e qualità delle decisioni.
                        Finestra ultimi <b>{data.window_days}</b> giorni · <b>{data.evaluations_in_window}</b> valutazioni golden.
                    </p>
                </div>
                <div className="flex border border-gray-200" data-testid="window-selector">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            data-testid={`window-${d}`}
                            onClick={() => setDays(d)}
                            className={`h-10 px-4 text-xs font-bold uppercase tracking-wider border-r last:border-r-0 border-gray-200 ${
                                days === d ? "bg-black text-white" : "bg-white hover:bg-gray-100"
                            }`}
                        >
                            {d}g
                        </button>
                    ))}
                </div>
            </div>

            {/* EXECUTIVE STRIP */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-6" data-testid="executive-strip">
                <ExecCell
                    icon={Target}
                    label="Investment Quality Score"
                    value={`${exec.investment_quality_score}`}
                    suffix="/100"
                    cls={qualityColor(exec.investment_quality_score)}
                />
                <ExecCell
                    icon={CheckCircle}
                    label="Decision Accuracy"
                    value={`${exec.decision_accuracy}%`}
                    cls={qualityColor(exec.decision_accuracy)}
                />
                <ExecCell
                    icon={Lightning}
                    label="Pipeline Success"
                    value={`${exec.pipeline_success_rate}%`}
                    cls={qualityColor(exec.pipeline_success_rate)}
                    sub={`${exec.completed} ok / ${exec.errored} errori`}
                />
                <ExecCell
                    icon={TrendUp}
                    label="Analisi processate"
                    value={`${exec.analyses_processed}`}
                />
            </div>

            {/* AGENT QUALITY */}
            <div className="bg-white border border-gray-200 p-6 mb-6" data-testid="agent-quality-grid">
                <div className="overline mb-4">Qualità per agente</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-gray-200">
                    <AgentMetric label="Doc Reader Accuracy" value={agentQ.document_reader_extraction_accuracy} unit="%" higherBetter />
                    <AgentMetric label="Legal Risk F1" value={agentQ.legal_risk_f1} higherBetter />
                    <AgentMetric label="Urban Risk F1" value={agentQ.urban_risk_f1} higherBetter />
                    <AgentMetric label="Market MAPE" value={agentQ.market_valuation_mape_pct} unit="%" higherBetter={false} />
                    <AgentMetric label="Bid MAPE" value={agentQ.bid_recommendation_mape_pct} unit="%" higherBetter={false} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* SCORE BAND DISTRIBUTION */}
                <div className="bg-white border border-gray-200 p-6 lg:col-span-1" data-testid="band-distribution">
                    <div className="overline mb-3">Distribuzione score band</div>
                    {bandData.length === 0 ? (
                        <p className="text-xs text-gray-400 mt-4">Nessuna analisi completata</p>
                    ) : (
                        <>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={bandData} dataKey="value" innerRadius={50} outerRadius={80}>
                                        {bandData.map((b, i) => (<Cell key={i} fill={b.color} />))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <ul className="mt-3 space-y-1.5">
                                {bandData.map((b) => (
                                    <li key={b.name} className="flex items-center justify-between text-xs">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2" style={{ background: b.color }} />
                                            {b.name}
                                        </span>
                                        <span className="font-mono-tight font-bold">{b.value}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                {/* QUALITY TREND */}
                <div className="bg-white border border-gray-200 p-6 lg:col-span-2" data-testid="quality-trend">
                    <div className="overline mb-3">Trend Quality Score</div>
                    {data.trend.length === 0 ? (
                        <p className="text-xs text-gray-400 mt-4">Esegui valutazioni golden per popolare il trend.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <LineChart data={data.trend} margin={{ top: 10, right: 20, bottom: 0, left: -20 }}>
                                <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#6B7280" }} />
                                <Tooltip />
                                <Line type="monotone" dataKey="avg_quality" stroke="#000000" strokeWidth={2} dot={{ r: 3, fill: "#000" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* AGENT TABLE */}
            <div className="bg-white border border-gray-200 mb-6" data-testid="agent-operational-table">
                <div className="p-6 pb-2">
                    <div className="overline mb-3">Operative · per agente (audit trail)</div>
                </div>
                {agentRows.length === 0 ? (
                    <p className="px-6 pb-6 text-xs text-gray-400">Nessun run registrato nella finestra.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="border-t border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left p-3 font-bold">Agente</th>
                                <th className="text-right p-3 font-bold">Success rate</th>
                                <th className="text-right p-3 font-bold">Retry rate</th>
                                <th className="text-right p-3 font-bold">Avg time (s)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {agentRows.map((r) => (
                                <tr key={r.agent} className="border-b border-gray-100 last:border-0">
                                    <td className="p-3 font-medium">{r.agent}</td>
                                    <td className={`p-3 text-right font-mono-tight font-bold ${qualityColor(r.success_rate)}`}>{r.success_rate}%</td>
                                    <td className={`p-3 text-right font-mono-tight ${r.retry_rate > 5 ? "text-red-600" : "text-gray-600"}`}>{r.retry_rate}%</td>
                                    <td className="p-3 text-right font-mono-tight">{r.avg_seconds}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* TOP ERRORS - CLICKABLE FOR DRILL-DOWN */}
            {data.top_error_categories.length > 0 && (
                <div className="bg-white border border-gray-200 p-6 mb-6" data-testid="top-errors">
                    <div className="overline mb-3">Top categorie di errore (clicca per drill-down)</div>
                    <ResponsiveContainer width="100%" height={Math.max(220, data.top_error_categories.length * 32)}>
                        <BarChart layout="vertical" data={data.top_error_categories} margin={{ top: 0, right: 16, bottom: 0, left: 60 }}
                                  onClick={(e) => e?.activeLabel && openDrill(e.activeLabel)}>
                            <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7280" }} />
                            <YAxis dataKey="category" type="category" tick={{ fontSize: 11, fill: "#94A3B8" }} width={150} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#DC2626" style={{ cursor: "pointer" }} />
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {data.top_error_categories.map((e) => (
                            <button
                                key={e.category}
                                data-testid={`drill-${e.category}`}
                                onClick={() => openDrill(e.category)}
                                className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-gray-300 hover:border-black hover:bg-gray-50"
                            >
                                {e.category} · {e.count}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* AGENT PERFORMANCE TRENDS */}
            {trends && trends.series.length > 0 && (
                <div className="bg-white border border-gray-200 p-6 mb-6" data-testid="agent-trends">
                    <div className="overline mb-3">Agent Performance Trends · per-agent F1 & MAPE nel tempo</div>
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={trends.series} margin={{ top: 10, right: 20, bottom: 0, left: -20 }}>
                            <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#6B7280" }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="legal_f1" stroke="#3B82F6" strokeWidth={1.5} dot={{ r: 2 }} name="Legal F1" />
                            <Line type="monotone" dataKey="urban_f1" stroke="#10B981" strokeWidth={1.5} dot={{ r: 2 }} name="Urban F1" />
                            <Line type="monotone" dataKey="extraction" stroke="#F59E0B" strokeWidth={1.5} dot={{ r: 2 }} name="Doc Extraction %" />
                            <Line type="monotone" dataKey="decision_match" stroke="#000000" strokeWidth={2} dot={{ r: 2 }} name="Decision Match %" />
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-wider font-bold">
                        <LegendDot color="#3B82F6" label="Legal F1" />
                        <LegendDot color="#10B981" label="Urban F1" />
                        <LegendDot color="#F59E0B" label="Doc Extraction" />
                        <LegendDot color="#000000" label="Decision Match" />
                    </div>
                </div>
            )}

            {/* DRILL-DOWN PANEL */}
            {drillCategory && drillData && (
                <div className="bg-black text-white p-6 mb-6" data-testid="drill-down-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="overline text-white/60 mb-1">Drill-down · {drillCategory}</div>
                            <div className="font-display-black text-2xl">{drillData.count} valutazione/i impattata/e</div>
                        </div>
                        <button onClick={() => { setDrillCategory(null); setDrillData(null); }} className="border border-white/30 hover:border-white h-9 px-3 text-xs font-bold uppercase tracking-wider">
                            Chiudi
                        </button>
                    </div>
                    {drillData.items.length === 0 ? (
                        <p className="text-sm text-white/70">Nessuna valutazione trovata.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="border-b border-white/20 text-xs uppercase tracking-wider text-white/60">
                                <tr>
                                    <th className="text-left p-2">Caso golden</th>
                                    <th className="text-left p-2">Provincia</th>
                                    <th className="text-left p-2">Difficoltà</th>
                                    <th className="text-right p-2">Quality</th>
                                    <th className="text-right p-2">Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {drillData.items.map((it) => (
                                    <tr key={it.evaluation_id} className="border-b border-white/10 last:border-0">
                                        <td className="p-2">{it.title}</td>
                                        <td className="p-2 text-white/70">{it.province}</td>
                                        <td className="p-2 text-white/70">{it.difficulty}</td>
                                        <td className={`p-2 text-right font-mono-tight font-bold ${qualityColor(it.quality_score)}`}>{it.quality_score?.toFixed(0)}</td>
                                        <td className="p-2 text-right text-white/60 font-mono-tight text-xs">{new Date(it.created_at).toLocaleDateString("it-IT")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <div className="mt-6 text-[10px] text-gray-500">
                Avg pipeline time: <b className="font-mono-tight">{data.operational.avg_pipeline_seconds}s</b>
            </div>
        </Layout>
    );
}

function ExecCell({ icon: Icon, label, value, suffix, cls = "", sub }) {
    return (
        <div className="bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
                <Icon size={12} weight="bold" className="text-gray-500" />
                <div className="overline">{label}</div>
            </div>
            <div className={`font-display-black text-3xl ${cls}`}>
                {value}
                {suffix && <span className="text-base text-gray-400">{suffix}</span>}
            </div>
            {sub && <div className="text-[10px] text-gray-500 font-mono-tight mt-1">{sub}</div>}
        </div>
    );
}

function AgentMetric({ label, value, unit = "", higherBetter = true }) {
    const num = value == null ? null : Number(value);
    let cls = "text-gray-500";
    if (num != null) {
        if (higherBetter) {
            cls = num >= 70 ? "text-emerald-600" : num >= 50 ? "text-yellow-700" : "text-red-600";
        } else {
            cls = num <= 15 ? "text-emerald-600" : num <= 30 ? "text-yellow-700" : "text-red-600";
        }
    }
    return (
        <div className="bg-white p-4">
            <div className="overline mb-1.5">{label}</div>
            <div className={`font-display-black text-2xl ${cls}`}>
                {num != null ? num : "—"}<span className="text-sm text-gray-400">{unit}</span>
            </div>
        </div>
    );
}

function LegendDot({ color, label }) {
    return (
        <span className="flex items-center gap-1.5 text-gray-700">
            <span className="w-2.5 h-2.5" style={{ background: color }} />
            {label}
        </span>
    );
}
